'use client';
import React from 'react';
import { X, SlidersHorizontal } from 'lucide-react';
import { PROPERTY_TYPES } from './mockData';
import type { ListingMode } from './mockData';
import { useLocale } from '@/contexts/LocaleContext';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedCity: string;
  setSelectedCity: (v: string) => void;
  selectedType: string;
  setSelectedType: (v: string) => void;
  selectedMode: 'all' | ListingMode;
  setSelectedMode: (v: 'all' | ListingMode) => void;
  priceMin: string;
  setPriceMin: (v: string) => void;
  priceMax: string;
  setPriceMax: (v: string) => void;
  furnished: boolean | null;
  setFurnished: (v: boolean | null) => void;
  onClear: () => void;
}

export default function FilterSidebar({
  open, onClose,
  selectedCity, setSelectedCity,
  selectedType, setSelectedType,
  selectedMode, setSelectedMode,
  priceMin, setPriceMin,
  priceMax, setPriceMax,
  furnished, setFurnished,
  onClear,
}: Props) {
  const { locale } = useLocale();
  const cities = locale.cities;
  const isArabic = locale.language === 'ar';

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`fixed top-0 left-0 h-full w-80 bg-white z-50 shadow-modal transition-transform duration-300 ease-in-out flex flex-col ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-primary-500" />
            <span className="font-600 text-gray-900">{isArabic ? 'تصفية النتائج' : 'Filter Results'}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Mode */}
          <div>
            <label className="block text-xs font-600 text-gray-500 uppercase tracking-wide mb-2">
              {isArabic ? 'نوع الإعلان' : 'Listing Type'}
            </label>
            <div className="flex gap-2">
              {(['all', 'sale', 'rent'] as const).map(m => (
                <button
                  key={`filter-mode-${m}`}
                  onClick={() => setSelectedMode(m)}
                  className={`flex-1 py-2 text-sm font-600 rounded-lg border transition-all ${
                    selectedMode === m
                      ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
                  }`}
                >
                  {m === 'all' ? (isArabic ?'الكل' : 'All')
                    : m === 'sale' ? (isArabic ?'للبيع' : 'For Sale')
                      : (isArabic ? 'للإيجار' : 'For Rent')}
                </button>
              ))}
            </div>
          </div>

          {/* City */}
          <div>
            <label className="block text-xs font-600 text-gray-500 uppercase tracking-wide mb-2">
              {isArabic ? 'المدينة' : 'City'}
            </label>
            <div className="space-y-1">
              {cities.map(city => (
                <button
                  key={`filter-city-${city}`}
                  onClick={() => setSelectedCity(city)}
                  className={`w-full text-right px-3 py-2 text-sm rounded-lg transition-colors ${
                    selectedCity === city
                      ? 'bg-primary-50 text-primary-600 font-600' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {city}
                </button>
              ))}
            </div>
          </div>

          {/* Property Type */}
          <div>
            <label className="block text-xs font-600 text-gray-500 uppercase tracking-wide mb-2">
              {isArabic ? 'نوع العقار' : 'Property Type'}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PROPERTY_TYPES.map(t => (
                <button
                  key={`filter-type-${t.value}`}
                  onClick={() => setSelectedType(t.value)}
                  className={`py-2 text-sm font-500 rounded-lg border transition-all ${
                    selectedType === t.value
                      ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <label className="block text-xs font-600 text-gray-500 uppercase tracking-wide mb-2">
              {isArabic ? `نطاق السعر (${locale.currencySymbol})` : `Price Range (${locale.currencySymbol})`}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder={isArabic ? 'من' : 'Min'}
                value={priceMin}
                onChange={e => setPriceMin(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right"
              />
              <span className="text-gray-400 text-sm">—</span>
              <input
                type="number"
                placeholder={isArabic ? 'إلى' : 'Max'}
                value={priceMax}
                onChange={e => setPriceMax(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right"
              />
            </div>
          </div>

          {/* Furnished */}
          <div>
            <label className="block text-xs font-600 text-gray-500 uppercase tracking-wide mb-2">
              {isArabic ? 'الأثاث' : 'Furnished'}
            </label>
            <div className="flex gap-2">
              {[
                { val: null, label: isArabic ? 'الكل' : 'All' },
                { val: true, label: isArabic ? 'مفروشة' : 'Furnished' },
                { val: false, label: isArabic ? 'غير مفروشة' : 'Unfurnished' },
              ].map(opt => (
                <button
                  key={`filter-furnished-${opt.label}`}
                  onClick={() => setFurnished(opt.val)}
                  className={`flex-1 py-2 text-xs font-600 rounded-lg border transition-all ${
                    furnished === opt.val
                      ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClear}
            className="flex-1 py-2.5 text-sm font-600 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {isArabic ? 'مسح الكل' : 'Clear All'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-600 text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
          >
            {isArabic ? 'عرض النتائج' : 'Show Results'}
          </button>
        </div>
      </div>
    </>
  );
}